/* BRRWare listings share the same physical cups. Reuse only an exact color and
 * source-variant match from their published Cup Library profiles. New listing
 * prices, stock, order variant IDs and production geometry remain independent. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.GroomsmanCupLibrary = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const ENDPOINT = 'https://images.jaxengraving.com/functions/v1/studio-cups';
  const SOURCES = Object.freeze({
    '15293885677821': 'custom-engraved-3-in-1-can-cooler-fits-cans-slim-cans-bottles',
    '15361223983357': 'design-your-own-3-in-1-can-cooler'
  });
  const id = value => typeof value === 'string' && /^[1-9]\d{0,23}$/.test(value);
  const uuid = value => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  const finite = (value, min, max) => typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
  const colorKey = value => typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
  const fail = () => { throw new Error('groomsman_cup_library_invalid'); };
  const empty = () => ({ photos: [], variantPhotoMap: {}, swatches: {} });
  function rectangle(value) {
    if (!value || !finite(value.x, 0, 99) || !finite(value.y, 0, 99) || !finite(value.width, 1, 100) || !finite(value.height, 1, 100) || value.x + value.width > 100.00001 || value.y + value.height > 100.00001) fail();
    return Object.fromEntries(['x', 'y', 'width', 'height'].map(key => [key, value[key] / 100]));
  }
  function publishedPhoto(item, variant, sideName = 'front') {
    const profile = item.profile, side = profile[sideName], photo = item.photos?.[side?.photoId];
    if (!uuid(item.id) || !uuid(item.publishedRevision) || !Number.isSafeInteger(item.revision) || item.revision < 1 || profile.version !== 1 ||
        !side || !uuid(side.photoId) || !finite(side.widthMm, 1, 304.8) || !finite(side.heightMm, 6.35, 304.8) ||
        typeof side.measured !== 'boolean' || typeof side.previewOnly !== 'boolean' || (side.calibrationMode !== undefined && side.calibrationMode !== 'independent')) fail();
    const crop = rectangle(side.crop), stageArea = rectangle(side.area);
    if (side.calibrationMode !== 'independent' && Math.abs((side.widthMm / side.heightMm) / (stageArea.width / stageArea.height) - 1) > .005) fail();
    if (!photo || photo.id !== side.photoId || !['image/png', 'image/jpeg'].includes(photo.mime) || !Number.isInteger(photo.width) || !Number.isInteger(photo.height) || photo.width < 1 || photo.height < 1 || photo.width * photo.height > 40000000) fail();
    let url = new URL(photo.url);
    const endpoint = new URL(ENDPOINT);
    if (![endpoint.origin, 'https://xmpqogywwhxeaxujcdmj.supabase.co'].includes(url.origin) || url.pathname !== endpoint.pathname || url.username || url.password || url.hash || url.searchParams.size !== 3 || url.searchParams.get('action') !== 'photo' || url.searchParams.get('revisionId') !== item.publishedRevision || url.searchParams.get('photoId') !== photo.id) fail();
    if (photo.previewUrl) {
      const preview = new URL(photo.previewUrl);
      if (preview.origin !== 'https://cdn.shopify.com' || !preview.pathname.startsWith('/s/files/') || preview.username || preview.password || preview.hash || preview.searchParams.get('width') !== '1600' || [...preview.searchParams.keys()].some(key => !['width', 'v'].includes(key))) fail();
      url = preview;
    }
    const width = photo.width * crop.width, height = photo.height * crop.height, scale = 1 / Math.max(width, height);
    const stageCropRect = { x: (1 - width * scale) / 2, y: (1 - height * scale) / 2, width: width * scale, height: height * scale };
    return { id: `groomsman-cup-library-${item.publishedRevision}-${variant.id}`, productId: 'brrware', variantId: variant.id,
      size: '3-in-1', color: variant.color, side: sideName, width: photo.width, height: photo.height, crop, stageCropRect, stageArea,
      widthMm: side.widthMm, heightMm: side.heightMm, approximate: true, previewOnly: true,
      calibrationSource: { type: 'published-cup-library', cupId: item.id, revisionId: item.publishedRevision,
        sourceProductId: profile.productId, sourceVariantId: profile.variantId, side: sideName,
        sourceAreaMm: { widthMm: side.widthMm, heightMm: side.heightMm }, measured: side.measured }, url: url.href };
  }
  function sourceMap(options) {
    const sources = new Map();
    for (const product of options.sourceProducts || []) {
      if (!SOURCES[product.id] || SOURCES[product.id] !== product.handle || !Array.isArray(product.variants)) continue;
      for (const variant of product.variants) {
        if (!id(variant.id) || !colorKey(variant.color)) fail();
        const key = product.id + '/' + variant.id;
        if (sources.has(key)) fail();
        sources.set(key, { ...variant, productId: product.id });
      }
    }
    return sources;
  }
  function applyManifest(body, options) {
    const sideName = options.side || 'front';
    if (!['front', 'back'].includes(sideName)) fail();
    const result = empty(), sources = sourceMap(options), data = body?.data;
    if (!sources.size) return result;
    if (body?.ok !== true || data?.version !== 1 || !Array.isArray(data.items) || !Array.isArray(data.suppressed) || data.items.length + data.suppressed.length > 500 || !Array.isArray(options.variants)) fail();
    const items = new Map(), suppressed = new Set();
    for (const item of data.suppressed) {
      if (!id(item?.productId) || !id(item.variantId)) fail();
      const key = item.productId + '/' + item.variantId;
      if (suppressed.has(key)) fail();
      suppressed.add(key);
    }
    for (const item of data.items) {
      const profile = item?.profile;
      if (!id(profile?.productId) || !id(profile.variantId)) fail();
      const key = profile.productId + '/' + profile.variantId, source = sources.get(key);
      if (!source) continue;
      if (items.has(key) || suppressed.has(key) || profile.catalog?.handle !== SOURCES[profile.productId] ||
          profile.physicalName !== 'BRRWare 3-in-1 Can Cooler' || colorKey(profile.colorName) !== colorKey(source.color)) fail();
      items.set(key, item);
    }
    for (const variant of options.variants) {
      if (!id(String(variant.id)) || !colorKey(variant.color)) fail();
      // Source insertion order deliberately prefers the original listing.
      const source = [...sources.values()].find(item => colorKey(item.color) === colorKey(variant.color));
      if (!source) continue;
      const item = items.get(source.productId + '/' + source.id);
      if (!item) continue;
      const photo = publishedPhoto(item, variant, sideName);
      result.photos.push(photo); result.variantPhotoMap[variant.id] = photo.id;
      if (/^#[0-9a-f]{6}$/i.test(item.profile.colorHex)) result.swatches[variant.color] = item.profile.colorHex;
    }
    return result;
  }
  async function load(options) {
    const controller = new AbortController(), abort = () => controller.abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    if (options.signal?.aborted) abort();
    let timer;
    try {
      if (!sourceMap(options).size) return { ...empty(), status: 'disabled' };
      const request = (async () => {
        const response = await (options.fetchImpl || globalThis.fetch)(`${ENDPOINT}?action=manifest&assetHost=branded`, {
          signal: controller.signal, credentials: 'omit', redirect: 'error', cache: 'no-store', headers: { Accept: 'application/json' }
        });
        if (!response.ok || response.redirected || !/^application\/json(?:;|$)/i.test(response.headers.get('content-type') || '') || Number(response.headers.get('content-length')) > 2000000) fail();
        const text = await response.text();
        if (text.length > 2000000 || controller.signal.aborted) fail();
        return applyManifest(JSON.parse(text), options);
      })();
      const timeout = new Promise((_, reject) => { timer = setTimeout(() => { abort(); reject(new Error('groomsman_cup_library_timeout')); }, options.timeoutMs ?? 5000); });
      return { ...await Promise.race([request, timeout]), status: 'loaded' };
    } catch { return { ...empty(), status: 'fallback' }; }
    finally { clearTimeout(timer); options.signal?.removeEventListener('abort', abort); }
  }
  return Object.freeze({ applyManifest, load });
});
